import { AnimatePresence, motion } from "motion/react";
import { useEffect, useRef, useState } from "react";
import { formatClock, type Clip } from "@mdj/shared";
import { api } from "../lib/api";
import { Button, GlassCard } from "../components/Glass";

const R = 42, C = 2 * Math.PI * R;

export function CutStep({ clip: initial, onDone, onRedo }: { clip: Clip; onDone: (clip: Clip) => void; onRedo: () => void }) {
  const [clip, setClip] = useState(initial);
  const [pct, setPct] = useState(4);
  const [playing, setPlaying] = useState(false);
  const [src, setSrc] = useState<string | null>(null);
  const audio = useRef<HTMLAudioElement>(null);

  // Fetch the clip bytes once ready (authenticated), play from an object URL.
  useEffect(() => {
    if (clip.status !== "ready") return;
    let url: string | null = null;
    api.clipAudioUrl(clip).then((u) => { url = u; setSrc(u); }).catch(() => setSrc(null));
    return () => { if (url?.startsWith("blob:")) URL.revokeObjectURL(url); };
  }, [clip.id, clip.status]);

  // Poll until the worker reports a terminal state.
  useEffect(() => {
    if (clip.status === "ready" || clip.status === "failed") return;
    const t = setInterval(async () => {
      try { setClip(await api.getClip(clip.id)); } catch { /* transient; keep polling */ }
    }, 700);
    return () => clearInterval(t);
  }, [clip.id, clip.status]);

  // Progress is a feel, not a measurement: creep toward 90 while working,
  // snap to 100 when ready.
  useEffect(() => {
    if (clip.status === "ready") { setPct(100); return; }
    if (clip.status === "failed") return;
    const t = setInterval(() => setPct((p) => Math.min(90, p + (90 - p) * 0.08 + 0.4)), 200);
    return () => clearInterval(t);
  }, [clip.status]);

  const ready = clip.status === "ready";
  const label = { queued: "Queued", extracting: "Cutting the audio", ready: "Ready", failed: "Couldn't cut that one" }[clip.status];

  const toggle = () => {
    const a = audio.current; if (!a) return;
    if (a.paused) { void a.play(); setPlaying(true); } else { a.pause(); a.currentTime = 0; setPlaying(false); }
  };

  return (
    <GlassCard key="cut">
      <h2>{ready ? "Your clip" : "Cutting…"}</h2>
      <div className="progress">
        <motion.div className="ring" animate={ready ? { scale: [1, 1.08, 1] } : {}} transition={{ duration: 0.5 }}>
          <svg viewBox="0 0 100 100" width="96" height="96">
            <circle cx="50" cy="50" r={R} fill="none" stroke="rgba(255,255,255,0.12)" strokeWidth="8" />
            <motion.circle
              cx="50" cy="50" r={R} fill="none" strokeWidth="8" strokeLinecap="round"
              stroke={clip.status === "failed" ? "var(--danger)" : ready ? "var(--accent-2)" : "var(--accent)"}
              strokeDasharray={C} animate={{ strokeDashoffset: C * (1 - pct / 100) }} transition={{ type: "spring", stiffness: 60, damping: 20 }}
            />
          </svg>
          {ready ? (
            <Button primary icon aria-label={playing ? "Stop" : "Play clip"} onClick={toggle} disabled={!src} style={{ width: 64, height: 64, fontSize: 22 }}>{playing ? "■" : "▶"}</Button>
          ) : (
            <span className="pct">{Math.round(pct)}%</span>
          )}
        </motion.div>
        <div>
          <div style={{ fontWeight: 700 }}>{label}</div>
          <div className="hint" style={{ marginTop: 4 }}>
            {formatClock(clip.window.start)} → {formatClock(clip.window.stop)}
            {clip.title ? ` · ${clip.title}` : ""}
          </div>
          {clip.error && <div style={{ color: "var(--danger)", fontSize: 13, marginTop: 6 }}>{clip.error}</div>}
        </div>
        <AnimatePresence>
          {(playing || (!ready && clip.status !== "failed")) && (
            <motion.div className="bars" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} aria-hidden>
              {Array.from({ length: 14 }).map((_, i) => (
                <motion.i key={i} animate={{ height: [8, 12 + ((i * 7) % 28), 8] }} transition={{ duration: 0.6 + (i % 5) * 0.1, repeat: Infinity, ease: "easeInOut" }} />
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      {ready && src && <audio ref={audio} src={src} onEnded={() => setPlaying(false)} preload="auto" />}
      <div className="row">
        <Button ghost onClick={onRedo}>{ready ? "Re-cut" : "Cancel"}</Button>
        <Button primary disabled={!ready} onClick={() => onDone(clip)}>Choose player</Button>
      </div>
    </GlassCard>
  );
}
